/**
 * Marketing assets for the B2B landing pages — co-located with the module
 * (per the convention used by `app/modules/us-products-coming-soon/images/`
 * and matched against PR #3375). Each image is imported as an ES module so
 * Vite emits a hashed, content-addressed URL that works identically in dev
 * and production. The previous `packages/web/public/b2b/...` + early
 * `express.static` mount was dev-only and broke on prod builds.
 *
 * Both `brands.config.ts` and `profs.config.ts` import the `B2B_IMAGES` map
 * below and assign the URLs into their `assets` records — keeping the
 * asset-key indirection so component code stays config-driven.
 */

import filmCover from './film-cover.png';
import hero from './hero.png';
import indexHero from './index-hero.jpg';
import profHero from './prof-hero.jpg';
import profPersonaHomeowners from './prof-persona-homeowners.jpg';
import profPersonaSpecifiers from './prof-persona-specifiers.jpg';
import profPillarsMockup from './prof-pillars-mockup.jpg';
// Videos — Figma source rectangles 480:29812 (Media Band) and 512:24605
// (Media Program hero). Re-encoded from the manual Figma export down to
// `.mp4` (h.264, Safari + iOS) + `.webm` (VP9, Chrome / Firefox). Browser
// picks the first supported `<source>`, defaulting WebM ahead of MP4 since
// it's ~30 % smaller at equivalent quality. See `BUSINESS_SUBDOMAIN.md`.
import clientTestimonialsVideo from './client-testimonials.mp4';
import clientTestimonialsVideoWebm from './client-testimonials.webm';
import mediaProgramVideo from './media-program.mp4';
import mediaProgramVideoWebm from './media-program.webm';
// SVGs default to the React-component import per the project's Vite config
// (`{ ReactComponent as Foo }`); use `?url` to get the asset URL instead so
// these can be set as `<img src>`.
import heroLogomark from './hero-logomark.svg?url';
import leadFormBg from './lead-form-bg.png';
import leadModalImage from './lead-modal-image.png';
import marketHeadline from './market-headline.png';
import mediaBand from './media-band.png';
import mediaProgramHero from './media-program-hero.png';
import partners from './partners.png';
import partnersStoneHouse from './partners-stone-house.png';
import personaHomeowners from './persona-homeowners.png';
import personaSpecifiers from './persona-specifiers.png';
import pillarsProducts from './pillars-products.png';
import profileCard from './profile-card.png';
import profileHero from './profile-hero.png';
import profileMockupBg from './profile-mockup-bg.jpg';
import profileMockupHero from './profile-mockup-hero.jpg';
import statement from './statement.png';
import statsBg from './stats-bg.png';
import taggingMedia from './tagging-media.png';
import taggingProduct from './tagging-product.png';
import techContent from './tech-content.png';
import testimonial from './testimonial.png';
import testimonialChristianAnderson from './testimonial-christian-anderson.png';
import testimonialChristianProject from './testimonial-christian-project.png';
import testimonialDavidPonting from './testimonial-david-ponting.png';
import testimonialDavidProject from './testimonial-david-project.png';
import testimonialHamishMuir from './testimonial-hamish-muir.png';
import testimonialHamishProject from './testimonial-hamish-project.png';
import testimonialJosephMammone from './testimonial-joseph-mammone.png';
import testimonialJulianGuthrie from './testimonial-julian-guthrie.png';
import testimonialJulianProject from './testimonial-julian-project.png';
import testimonialNicoleProject from './testimonial-nicole-project.png';
import testimonialNicoleTrinity from './testimonial-nicole-trinity.png';
import testimonialThumb from './testimonial-thumb.png';
import wordmarkDark from './wordmark-dark.png';
import wordmarkModalLogomark from './wordmark-modal-logomark.svg?url';
import wordmarkWhite from './wordmark-white.png';

/**
 * Resolved URLs for every B2B asset. Keys match the camelCase asset keys
 * used in `brands.config.ts` / `profs.config.ts` (so configs can spread or
 * cherry-pick without renaming).
 */
export const B2B_IMAGES = {
    filmCover,
    hero,
    heroLogomark,
    indexHero,
    profHero,
    profPersonaHomeowners,
    profPersonaSpecifiers,
    profPillarsMockup,
    clientTestimonialsVideo,
    clientTestimonialsVideoWebm,
    mediaProgramVideo,
    mediaProgramVideoWebm,
    leadFormBg,
    leadModalImage,
    marketHeadline,
    mediaBand,
    mediaProgramHero,
    partners,
    partnersStoneHouse,
    personaHomeowners,
    personaSpecifiers,
    pillarsProducts,
    profileCard,
    profileHero,
    profileMockupBg,
    profileMockupHero,
    statement,
    statsBg,
    taggingMedia,
    taggingProduct,
    techContent,
    testimonial,
    testimonialChristianAnderson,
    testimonialChristianProject,
    testimonialDavidPonting,
    testimonialDavidProject,
    testimonialHamishMuir,
    testimonialHamishProject,
    testimonialJosephMammone,
    testimonialJulianGuthrie,
    testimonialJulianProject,
    testimonialNicoleProject,
    testimonialNicoleTrinity,
    testimonialThumb,
    wordmarkDark,
    wordmarkModalLogomark,
    wordmarkWhite,
} as const;
